import { LDFlagSet } from 'launchdarkly-js-client-sdk';
import { useFlags } from 'launchdarkly-react-client-sdk';

/**
 * Feature flag keys exactly as they are named in LaunchDarkly.
 *
 * The provider is configured with `useCamelCaseFlagKeys: false` (see `src/index.tsx`) so these keys
 * match the LaunchDarkly UI verbatim. Keeping the raw keys avoids the camel-case collisions the SDK
 * warns about and keeps LaunchDarkly's Code References feature able to find usages in this repo.
 */
export const FeatureFlags = {
    /** Controls visibility of the Lock/Unlock consignment context menu options. */
    ENABLE_UNLOCK_FEATURE: 'enable-unlock-feature',
    /** Opens draft consignments' "View eNVD" PDF in the in-app preview modal instead of a new tab. */
    PREVIEW_DRAFT_CONSIGNMENT_WEB: 'preview-draft-consignment-web',
} as const;

/**
 * The value type each flag serves, keyed by flag key. LaunchDarkly flags can be boolean, string or
 * number variations, so every new flag must be declared here with the type of its variations.
 *
 * A type alias rather than an interface on purpose: an interface is not assignable to the SDK's
 * index-signature `LDFlagSet`, which the provider's `flags` option expects.
 */
export type FeatureFlagValues = {
    [FeatureFlags.ENABLE_UNLOCK_FEATURE]: boolean;
    [FeatureFlags.PREVIEW_DRAFT_CONSIGNMENT_WEB]: boolean;
};

export type FeatureFlagKey = keyof FeatureFlagValues;

/**
 * Default flag values.
 *
 * These apply before LaunchDarkly responds and whenever it cannot be reached at all — no client-side
 * ID configured, network failure, or an offline PWA session.
 *
 * Choose each default as the value that is safe to serve during a LaunchDarkly outage. For a release
 * flag that is `false` — the feature stays hidden unless LaunchDarkly turns it on. For a kill switch
 * guarding an existing feature it is `true`, so an outage does not switch the feature off for everyone.
 * For a string or number flag it is the value the app used before the flag existed.
 *
 * Typed as {@link FeatureFlagValues}, so a flag declared there without a default here — or with a
 * default of the wrong type — fails to compile.
 *
 * This object is also passed as the provider's `flags` option, which restricts the client to
 * requesting and subscribing to only these flags rather than the whole environment.
 */
export const featureFlagDefaults: FeatureFlagValues = {
    [FeatureFlags.ENABLE_UNLOCK_FEATURE]: false,
    [FeatureFlags.PREVIEW_DRAFT_CONSIGNMENT_WEB]: false,
};

/**
 * Reads a single feature flag, typed by its entry in {@link FeatureFlagValues}.
 *
 * Falls back to the flag's entry in {@link featureFlagDefaults} whenever LaunchDarkly has not
 * supplied a value of the default's type — which covers the not-yet-initialised, unreachable, and
 * no-provider cases (the last of which is what unit tests see unless they mock the SDK), as well as a
 * flag whose variation type was changed in LaunchDarkly without updating this file.
 */
export const useFeatureFlag = <K extends FeatureFlagKey>(flagKey: K): FeatureFlagValues[K] => {
    const flags: LDFlagSet = useFlags();
    const value: unknown = flags[flagKey];
    const fallback = featureFlagDefaults[flagKey];

    return typeof value === typeof fallback ? (value as FeatureFlagValues[K]) : fallback;
};
